/**
 * Preview schedule data generator for add-crop modal
 * Generates schedule data in the same shape as useMultiCropScheduleData output
 * allowing reuse of existing timeline rendering logic
 */

// Canonical phase ordering by lifecycle stage
export const PHASE_ORDER = [
  // Indoor/pre-transplant
  'stratification',  // Cold treatment before germination
  'germination',     // Seed sprouting
  'seedling',        // Indoor development
  'rooting',         // For cuttings
  // Outdoor growth
  'maturing',        // Core outdoor phase (tied to DTM)
  'vegetative',      // Leaf/stem development
  'budding',         // Bud formation
  'flowering',       // Flower stage
  'ripening',        // Fruit development
  // Harvest window
  'mature',          // Post-DTM harvest window (duration based on growth type)
  // Post-harvest
  'curing',          // Post-harvest treatment
  'decomposition',   // End of lifecycle (annuals/biennials)
  'dormant'          // Winter dormancy (perennials only)
]

// Indoor phases that occur before transplant
const INDOOR_PHASES = ['stratification', 'germination', 'seedling', 'rooting', 'curing']

// Default phase durations when phase types not available from DB
export const DEFAULT_PHASE_DURATIONS = {
  germination: 7,
  stratification: 60,
  seedling: 14,
  rooting: 21,
  maturing: 45,
  mature: 14,  // Default harvest window (2 weeks)
  curing: 14,
  dormant: 90  // Default winter dormancy (3 months)
}

/**
 * Add days to a date
 */
function addDays(date, days) {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

/**
 * Add weeks to a date
 */
function addWeeks(date, weeks) {
  return addDays(date, weeks * 7)
}

/**
 * Get phase duration from phase types array or use default
 */
export function getPhaseDuration(phaseCode, phaseTypes) {
  const phaseType = phaseTypes?.find(pt => pt.code === phaseCode)
  return phaseType?.typical_duration_days ?? DEFAULT_PHASE_DURATIONS[phaseCode] ?? 14
}

/**
 * Get phase name from phase types array or generate from code
 */
export function getPhaseName(phaseCode, phaseTypes) {
  const phaseType = phaseTypes?.find(pt => pt.code === phaseCode)
  if (phaseType?.name) return phaseType.name

  // Convert code to title case: 'germination' -> 'Germination'
  return phaseCode
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Create a phase object matching the DB schema shape
 */
function createPhase(phaseCode, startDate, durationDays, step, phaseTypes) {
  return {
    id: `preview-phase-${step}`,
    phase_type_code: phaseCode,
    phase_type: {
      code: phaseCode,
      name: getPhaseName(phaseCode, phaseTypes),
      typical_duration_days: durationDays
    },
    est_started_at: startDate.toISOString(),
    est_duration: `${durationDays} days`,
    started_at: null,
    duration: null,
    step,
    state: 'scheduled',
  }
}

/**
 * Generate phases for a schedule.
 * Exported for use in end-season carry-over logic.
 *
 * @see generatePreviewScheduleData for the higher-level wrapper
 *
 * Generate phases for the preview schedule
 * Supports dynamic phase selection while maintaining timing relationships
 *
 * Key timing:
 * - Stratification occurs BEFORE indoor offset (extends timeline backward)
 * - Indoor phases (germination, seedling) use their default durations
 * - Adding/removing phases extends/contracts timeline, doesn't compress existing phases
 * - Maturing phase duration equals DTM (from transplant to harvest)
 * - Mature phase (harvest window) duration depends on growth type:
 *   - Determinate: uses archetype.harvest_window (weeks), capped at first frost
 *   - Indeterminate: extends until first frost date
 * - Post-harvest phases are sequential after harvest window
 */
export function generatePhases(plantingDate, transplantDate, harvestDate, phaseTypes, selectedPhaseCodes, { growthType, harvestWindow, firstFrostDate, propagationCode, lifecycleType, isFirstYear } = {}) {
  const phases = []
  let step = 1
  const isPerennial = lifecycleType === 'perennial'

  // Propagation-aware default phases when none explicitly selected
  const getDefaultPhases = (code) => {
    if (isPerennial) {
      if (!isFirstYear) return ['maturing', 'mature', 'dormant']
      // First year perennial: same as annual but with dormant instead of decomposition
      if (code === 'transplant') return ['maturing', 'mature', 'dormant']
      if (code === 'cutting') return ['rooting', 'maturing', 'mature', 'dormant']
      if (code === 'outdoor_sow') return ['germination', 'maturing', 'dormant']
      return ['germination', 'seedling', 'maturing', 'dormant']
    }
    if (code === 'transplant') return ['maturing', 'mature']
    if (code === 'cutting') return ['rooting', 'maturing', 'mature']
    if (code === 'outdoor_sow') return ['germination', 'maturing']
    return ['germination', 'seedling', 'maturing']
  }

  const phasesToGenerate = selectedPhaseCodes && selectedPhaseCodes.length > 0
    ? selectedPhaseCodes
    : getDefaultPhases(propagationCode)

  // Sort phases by PHASE_ORDER
  const sortedPhases = [...phasesToGenerate].sort(
    (a, b) => PHASE_ORDER.indexOf(a) - PHASE_ORDER.indexOf(b)
  )

  // Separate phase categories
  const hasStratification = sortedPhases.includes('stratification')
  const indoorPhases = sortedPhases.filter(code =>
    INDOOR_PHASES.includes(code) && code !== 'stratification'
  )
  const hasMaturing = sortedPhases.includes('maturing')
  const hasMature = sortedPhases.includes('mature')
  const outdoorPhases = sortedPhases.filter(code =>
    !INDOOR_PHASES.includes(code) && code !== 'maturing' && code !== 'mature' && code !== 'curing' && code !== 'decomposition' && code !== 'dormant'
  )
  const postHarvestPhases = sortedPhases.filter(code =>
    code === 'curing' || code === 'decomposition'
  )
  const hasClosingDormant = isPerennial && sortedPhases.includes('dormant')

  // For subsequent-year perennials: opening dormant before the growing season
  // Duration: Jan 1 of schedule year → last frost date
  const scheduleYear = new Date(plantingDate).getFullYear()
  if (isPerennial && !isFirstYear && hasClosingDormant) {
    const openingStart = new Date(scheduleYear, 0, 1)  // Jan 1
    const openingEnd = new Date(plantingDate)           // Last frost (planting date)
    const openingDays = Math.max(1, Math.floor((openingEnd - openingStart) / (1000 * 60 * 60 * 24)))
    phases.push(createPhase('dormant', openingStart, openingDays, step++, phaseTypes))
  }

  // Calculate start date for stratification (before planting date)
  let currentDate = new Date(plantingDate)
  if (hasStratification) {
    const stratificationDays = getPhaseDuration('stratification', phaseTypes)
    const stratificationStart = addDays(plantingDate, -stratificationDays)
    phases.push(createPhase('stratification', stratificationStart, stratificationDays, step++, phaseTypes))
  }

  // Generate indoor phases with their default durations (no scaling)
  for (const phaseCode of indoorPhases) {
    const days = getPhaseDuration(phaseCode, phaseTypes)
    phases.push(createPhase(phaseCode, currentDate, days, step++, phaseTypes))
    currentDate = addDays(currentDate, days)
  }

  // Anchor maturing to transplant date when no indoor phases precede it
  // (covers transplant, cutting without rooting, outdoor_sow without germination, etc.)
  // When indoor phases exist (e.g. rooting for cuttings), maturing follows directly after them
  if (indoorPhases.length === 0 && !hasStratification) {
    currentDate = new Date(transplantDate)
  }

  // Generate maturing phase (tied to DTM)
  if (hasMaturing) {
    const maturingDays = Math.max(1, Math.floor((harvestDate - currentDate) / (1000 * 60 * 60 * 24)))
    phases.push(createPhase('maturing', currentDate, maturingDays, step++, phaseTypes))
    currentDate = addDays(currentDate, maturingDays)
  }

  // Generate other outdoor phases (sequential with default durations)
  for (const phaseCode of outdoorPhases) {
    const days = getPhaseDuration(phaseCode, phaseTypes)
    phases.push(createPhase(phaseCode, currentDate, days, step++, phaseTypes))
    currentDate = addDays(currentDate, days)
  }

  // Generate mature phase (harvest window - duration based on growth type)
  if (hasMature && firstFrostDate) {
    let matureDays
    const daysUntilFrost = Math.floor((firstFrostDate - currentDate) / (1000 * 60 * 60 * 24))
    const harvestWindowDays = (harvestWindow ?? 3) * 7

    if (growthType === 'indeterminate') {
      // Indeterminate: extends until first frost, or falls back to harvest window if already past frost
      matureDays = daysUntilFrost > 0 ? Math.max(1, daysUntilFrost) : harvestWindowDays
    } else {
      // Determinate: use harvest_window (weeks), capped at first frost if frost comes first
      matureDays = daysUntilFrost > 0 ? Math.min(harvestWindowDays, daysUntilFrost) : harvestWindowDays
    }
    phases.push(createPhase('mature', currentDate, matureDays, step++, phaseTypes))
    currentDate = addDays(currentDate, matureDays)
  }

  // Generate post-harvest phases (sequential after harvest, not including dormant)
  for (const phaseCode of postHarvestPhases) {
    const days = getPhaseDuration(phaseCode, phaseTypes)
    phases.push(createPhase(phaseCode, currentDate, days, step++, phaseTypes))
    currentDate = addDays(currentDate, days)
  }

  // Perennial closing dormant: extends from after harvest to Dec 31 of schedule year
  if (hasClosingDormant) {
    const yearEnd = new Date(scheduleYear, 11, 31)  // Dec 31
    const dormantDays = Math.max(1, Math.floor((yearEnd - currentDate) / (1000 * 60 * 60 * 24)))
    phases.push(createPhase('dormant', currentDate, dormantDays, step++, phaseTypes))
  }

  return phases
}

/**
 * Convert a template DTM to a farm-specific DTM using the heat ratio between
 * the farm's actual daily GDD accumulation and the collection's reference rate.
 *
 * @param {Object} params
 * @param {number} params.dtm - Template days to maturity
 * @param {number} params.gddBase - GDD base temperature (5 or 10)
 * @param {Object} params.collection - Collection type object with avg_daily_gdd5/avg_daily_gdd10
 * @param {Object} params.farmClimate - Org climate record with avg_daily_gdd5/avg_daily_gdd10
 * @returns {number} Adjusted DTM (falls back to original if data missing)
 */
export function convertDtmForClimate({ dtm, gddBase, collection, farmClimate }) {
  if (!dtm || !collection || !farmClimate) return dtm
  const collectionAvg = gddBase === 5 ? collection.avg_daily_gdd5 : collection.avg_daily_gdd10
  const farmAvg = gddBase === 5 ? farmClimate.avg_daily_gdd5 : farmClimate.avg_daily_gdd10
  if (!collectionAvg || !farmAvg) return dtm
  return Math.round(dtm * (collectionAvg / farmAvg))
}

/**
 * Generate preview schedule data from archetype and form values
 *
 * @param {Object} params
 * @param {Object} params.archetype - Crop archetype with frost-relative offsets
 * @param {Object} params.species - Template crop species
 * @param {Object} params.overrides - User overrides { indoorOffset, outdoorOffset, dtm }
 * @param {Array} params.phaseTypes - Phase types from DB (optional)
 * @param {Array} params.selectedPhaseCodes - Selected phase codes (null = use defaults)
 * @param {string} params.growthType - Growth type ('determinate' or 'indeterminate')
 * @param {Object} params.collection - Collection type for the species (provides reference daily GDD)
 * @param {Object} params.farmClimate - Org climate record (provides actual daily GDD for DTM conversion)
 * @returns {Array} Schedule data array matching useMultiCropScheduleData output shape
 */
export function generatePreviewScheduleData({
  archetype,
  species,
  frostDates,
  overrides = {},
  phaseTypes = [],
  selectedPhaseCodes = null,
  growthType = null,
  propagationCode = null,
  lifecycleType = null,
  cropCreatedAt = null,
  successions = null,
  collection = null,
  farmClimate = null,
}) {
  const isValidDate = (d) => d instanceof Date && !isNaN(d)

  if (!archetype || !species) return null

  if (!isValidDate(frostDates?.lfd)) return null

  const year = new Date().getFullYear()

  // Determine if this is the first year for a perennial
  // First year: crop doesn't exist yet, or was created in the same year as the schedule
  const isFirstYear = !cropCreatedAt || new Date(cropCreatedAt).getFullYear() >= year

  // Calculate key dates
  const indoorOffset = overrides.indoorOffset ?? archetype.indoor_sow_offset ?? 0
  const outdoorOffset = overrides.outdoorOffset ?? archetype.outdoor_move_offset ?? 0
  const rawDtm = overrides.dtm ?? species.dtm ?? archetype.dtm ?? 60
  const gddBase = species.gdd_base ?? archetype.gdd_base ?? 10
  const dtm = convertDtmForClimate({ dtm: rawDtm, gddBase, collection, farmClimate })

  // Planting date = frost date + indoor offset (weeks)
  const plantingDate = addWeeks(frostDates.lfd, indoorOffset)

  // Transplant/outdoor date = frost date + outdoor offset (weeks)
  const transplantDate = addWeeks(frostDates.lfd, outdoorOffset)

  // DTM start depends on archetype setting
  const dtmStartDate = archetype.dtm_starts_at_transplant ? transplantDate : plantingDate

  // Harvest date = DTM start + days to maturity
  const harvestDate = addDays(dtmStartDate, dtm)

  // Determine effective growth type (user override or species default)
  const effectiveGrowthType = growthType ?? species.growth_code // TODO variety hierarchy override

  // Generate phases (transplantDate determines when vegetative starts)
  // TODO crop hierachy, pass harvest_window
  const phases = generatePhases(plantingDate, transplantDate, harvestDate, phaseTypes, selectedPhaseCodes, {
    growthType: effectiveGrowthType,
    harvestWindow: archetype.harvest_window,
    firstFrostDate: frostDates.ffd,
    propagationCode,
    lifecycleType,
    isFirstYear,
  })

  // Build schedule data in the same shape as useMultiCropScheduleData output
  const speciesData = {
    id: species.id,
    name: species.name,
    name_latin: species.name_latin
  }

  const baseCrop = {
    id: 'preview-crop',
    template_crop_species: speciesData,
    template_crop_varieties: null,
    count: 1,
    notes: 'Preview',
    organization_id: null
  }

  const baseSchedule = {
    id: 'preview-schedule',
    crop_id: 'preview-crop',
    planting_date: plantingDate.toISOString(),
    expected_harvest_date: harvestDate.toISOString(),
    actual_harvest_date: null,
    year,
    state: 'active',
    notes: null,
    cloned_from_schedule_id: null
  }

  const items = [{ crop: baseCrop, schedule: baseSchedule, phases }]

  // Generate succession preview items (offset by interval per succession)
  if (successions?.enabled && successions.count >= 1) {
    const intervalDays = successions.intervalDays ?? 14
    for (let i = 1; i <= successions.count; i++) {
      const offsetDays = i * intervalDays
      const offsetPhases = phases.map(phase => ({
        ...phase,
        id: `preview-phase-succ${i}-${phase.step}`,
        est_started_at: addDays(new Date(phase.est_started_at), offsetDays).toISOString(),
      }))
      const succCropId = `preview-succession-${i}`
      items.push({
        crop: {
          ...baseCrop,
          id: succCropId,
          parent_crop_id: 'preview-crop',
          succession: i,
          succession_interval_days: offsetDays,
          notes: `Succession ${i}`,
        },
        schedule: {
          ...baseSchedule,
          id: `preview-schedule-succ-${i}`,
          crop_id: succCropId,
          planting_date: addDays(plantingDate, offsetDays).toISOString(),
          expected_harvest_date: addDays(harvestDate, offsetDays).toISOString(),
        },
        phases: offsetPhases,
      })
    }
  }

  return items
}
